import { on, qs } from '../../core/dom';
import { ga4 } from '../../core/ga4';
import type { NavModuleInit } from '../../core/types';

const EXPANDED_WIDTH = 275;
const COLLAPSED_WIDTH = 65;
const MEDIA_QUERY = '(min-width: 960px)';

function treeLevelName(el: Element | null): string {
  const levelEl = el?.closest<HTMLElement>('[data-tree-level]');
  const level = levelEl ? Number(levelEl.dataset.treeLevel) : 0;
  return level >= 2 ? 'Tertiary' : level === 1 ? 'Secondary' : 'Primary';
}

function applyState(
  expanded: boolean,
  { pushContent = true }: { pushContent?: boolean } = {},
): void {
  const container = document.querySelector<HTMLElement>('#side-nav-ssr, .f1-sidenav-container');
  const sidenav = document.querySelector<HTMLElement>('.f1-sidenav');
  const mainContent = document.getElementById('f1-main-right-content');
  const chevron = document.querySelector<SVGElement>(
    'button[aria-label="Toggle sidebar"] svg, #toggleButton svg',
  );

  if (container) {
    container.classList.toggle('expanded', expanded);
    container.classList.toggle('collapsed', !expanded);
  }

  if (sidenav) {
    sidenav.classList.toggle('f1-sidenav--expanded', expanded);
    sidenav.classList.toggle('f1-sidenav--collapsed', !expanded);
    if (pushContent) {
      sidenav.classList.toggle('px-3', expanded);
      sidenav.classList.toggle('px-2', !expanded);
    }
  }

  if (mainContent && pushContent) {
    mainContent.classList.toggle('f1-main-right-content--expanded', expanded);
    mainContent.classList.toggle('f1-main-right-content--collapsed', !expanded);
  }

  if (chevron) {
    chevron.classList.toggle('rotate-180', !expanded);
  }

  if (pushContent) {
    document.documentElement.style.setProperty(
      '--main-right-content-padding',
      `${(expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH) + 1}px`,
    );
  }
}

function isDesktop(): boolean {
  return window.matchMedia(MEDIA_QUERY).matches;
}

function isExpandedNow(): boolean {
  return (
    document
      .querySelector<HTMLElement>('.f1-sidenav')
      ?.classList.contains('f1-sidenav--expanded') ?? false
  );
}

function highlightActive(): void {
  const path = location.pathname.replace(/\/$/, '') || '/';
  document.querySelectorAll<HTMLAnchorElement>('a.tree-item-link[href]').forEach((anchor) => {
    const treeItem = anchor.querySelector<HTMLElement>('.tree-item');
    if (!treeItem) return;
    const rawHref = anchor.getAttribute('href') ?? '';
    if (!rawHref || rawHref === '#' || rawHref.startsWith('javascript:')) {
      treeItem.classList.remove('tree-item--active');
      return;
    }
    let urlHost = '';
    let urlPath = rawHref;
    try {
      const u = new URL(anchor.href, location.origin);
      urlHost = u.host;
      urlPath = u.pathname.replace(/\/$/, '') || '/';
    } catch {}
    const sameHost = urlHost === '' || urlHost === location.host;
    treeItem.classList.toggle('tree-item--active', sameHost && urlPath === path);
  });
}

function activateTreeItem(anchor: HTMLAnchorElement, e: Event): void {
  const me = e as MouseEvent;
  if (me.metaKey || me.ctrlKey || me.shiftKey || (me.button !== undefined && me.button !== 0)) {
    return;
  }

  const treeItem = anchor.querySelector<HTMLElement>('.tree-item');
  if (!treeItem?.classList.contains('tree-item--has-children')) return;

  const sidenav = anchor.closest('.f1-sidenav');
  const isCollapsed = sidenav?.classList.contains('f1-sidenav--collapsed') ?? false;
  if (isCollapsed) return;

  const target = e.target as Element | null;
  if (!target?.closest('.sidenav-toggle-icon')) return;

  e.preventDefault();

  const isExpanded = treeItem.classList.toggle('tree-item--expanded');
  anchor.setAttribute('aria-expanded', String(isExpanded));
  const children = anchor.parentElement?.querySelector<HTMLElement>(
    ':scope > [data-tree-children]',
  );
  children?.classList.toggle('tree-item-children--expanded', isExpanded);

  ga4('controlClicked', {
    url: anchor.getAttribute('href') ?? '',
    text: `${treeLevelName(anchor)} Menu ${isExpanded ? 'Expanded' : 'Collapsed'}`,
  });
}

function bindExpandClicks(): void {
  document.querySelectorAll<HTMLAnchorElement>('a.tree-item-link').forEach((anchor) => {
    if (anchor.dataset.gnTreeBound === '1') return;
    anchor.dataset.gnTreeBound = '1';
    anchor.addEventListener('click', (e) => activateTreeItem(anchor, e));
  });
}

const init: NavModuleInit = ({ bus }) => {
  const container = qs<HTMLElement>('#side-nav-ssr, .f1-sidenav-container');
  if (!container) return;

  highlightActive();
  bindExpandClicks();

  const ssrExpanded = container.classList.contains('expanded');
  const initialExpanded = isDesktop() && ssrExpanded;

  applyState(initialExpanded);

  let hoverEnabled = !initialExpanded;
  let collapseTimer: ReturnType<typeof setTimeout> | undefined;

  const onEnter = () => {
    if (!isDesktop() || !hoverEnabled) return;
    if (collapseTimer) {
      clearTimeout(collapseTimer);
      collapseTimer = undefined;
    }
    if (!isExpandedNow()) applyState(true, { pushContent: false });
  };

  const onLeave = () => {
    if (!isDesktop() || !hoverEnabled) return;
    if (collapseTimer) clearTimeout(collapseTimer);
    collapseTimer = setTimeout(() => {
      collapseTimer = undefined;
      if (hoverEnabled && isExpandedNow()) applyState(false, { pushContent: false });
    }, 150);
  };

  const hoverZone = container.querySelector<HTMLElement>('.f1-sidenav');
  const offEnter = hoverZone ? on(hoverZone, 'mouseenter', onEnter) : () => undefined;
  const offLeave = hoverZone ? on(hoverZone, 'mouseleave', onLeave) : () => undefined;

  const toggle = qs<HTMLButtonElement>('#toggleButton, button[aria-label="Toggle sidebar"]');

  const onClick = () => {
    if (!isDesktop()) return;
    if (collapseTimer) {
      clearTimeout(collapseTimer);
      collapseTimer = undefined;
    }
    const next = !isExpandedNow();
    applyState(next);
    hoverEnabled = !next;
    ga4('controlClicked', {
      parentClassName: 'Navigation Toggle',
      text: next ? 'Expand' : 'Collapse',
    });
    bus.emit('side-nav:expand', { expanded: next });
  };

  const offClick = toggle ? on(toggle, 'click', onClick) : () => undefined;

  const mql = window.matchMedia(MEDIA_QUERY);
  const onMediaChange = (e: MediaQueryListEvent) => {
    if (!e.matches) {
      applyState(false);
    }
  };
  mql.addEventListener('change', onMediaChange);

  return {
    destroy: () => {
      offClick();
      offEnter();
      offLeave();
      if (collapseTimer) clearTimeout(collapseTimer);
      mql.removeEventListener('change', onMediaChange);
    },
  };
};

export default init;
